import { type FormEvent, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import { useAuth } from '../../contexts/AuthContext'
import { useLang } from '../../contexts/LangContext'
import { SIGN_IN_MUTATION } from '../../graphql/auth'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import FormError from '../../components/ui/FormError'
import type { CurrentUser } from '../../contexts/AuthContext'

interface SignInData {
  signIn: {
    user: CurrentUser | null
    token: string | null
    errors: string[]
  }
}

function validate(email: string, password: string) {
  const errs: Record<string, string> = {}
  if (!email) errs.email = 'Το email είναι υποχρεωτικό'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Μη έγκυρο email'
  if (!password) errs.password = 'Ο κωδικός είναι υποχρεωτικός'
  return errs
}

export default function SignIn() {
  const { signIn } = useAuth()
  const { t, locale } = useLang()
  const navigate = useNavigate()
  const location = useLocation()
  const from = (location.state as { from?: string } | null)?.from

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  const [mutate, { loading }] = useMutation<SignInData>(SIGN_IN_MUTATION)

  function handleBlur(field: string) {
    setTouched((t) => ({ ...t, [field]: true }))
    const errs = validate(email, password)
    setFieldErrors((prev) => ({ ...prev, [field]: errs[field] ?? '' }))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const errs = validate(email, password)
    if (Object.keys(errs).length) {
      setFieldErrors(errs)
      setTouched({ email: true, password: true })
      return
    }

    setServerErrors([])
    try {
      const { data } = await mutate({ variables: { email, password } })
      const result = data?.signIn
      if (!result) return

      if (result.errors.length) {
        setServerErrors(result.errors)
        return
      }

      if (result.token && result.user) {
        const signedInUser = await signIn(result.token, result.user)
        if (from) {
          navigate(from, { replace: true })
        } else if (signedInUser.admin) {
          navigate('/admin', { replace: true })
        } else if (signedInUser.role === 'provider') {
          navigate('/provider/dashboard', { replace: true })
        } else {
          navigate('/dashboard', { replace: true })
        }
      }
    } catch (err) {
      setServerErrors([t('Κάτι πήγε στραβά. Δοκιμάστε ξανά.', 'Something went wrong. Please try again.')])
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900">
            {t('Καλώς ήρθατε', 'Welcome back')}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {t('Συνδεθείτε στον λογαριασμό σας', 'Sign in to your account')}
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <FormError errors={serverErrors} />

            <Input
              type="email"
              label={t('Email', 'Email')}
              data-cy="signin-email"
              placeholder={locale === 'el' ? 'you@example.com' : 'you@example.com'}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => handleBlur('email')}
              error={touched.email ? fieldErrors.email : ''}
              required
              autoComplete="email"
            />

            <Input
              type="password"
              label={t('Κωδικός πρόσβασης', 'Password')}
              data-cy="signin-password"
              placeholder={locale === 'el' ? '••••••••' : '••••••••'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onBlur={() => handleBlur('password')}
              error={touched.password ? fieldErrors.password : ''}
              required
              autoComplete="current-password"
            />

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-xs text-primary-600 hover:underline">
                {t('Ξεχάσατε τον κωδικό;', 'Forgot password?')}
              </Link>
            </div>

            <Button type="submit" loading={loading} fullWidth size="lg" data-cy="signin-submit">
              {t('Σύνδεση', 'Sign In')}
            </Button>
          </form>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          {t('Δεν έχετε λογαριασμό;', "Don't have an account?")}{' '}
          <Link to="/signup" className="text-primary-600 font-medium hover:underline">
            {t('Εγγραφή', 'Sign Up')}
          </Link>
        </p>
        <p className="mt-2 text-center text-sm text-slate-500">
          {t('Είστε επαγγελματίας καθαρισμού;', 'A cleaning professional?')}{' '}
          <Link to="/provider/signup" className="text-secondary-600 font-medium hover:underline">
            {t('Εγγραφή ως επαγγελματίας', 'Register as a provider')}
          </Link>
        </p>
      </div>
    </div>
  )
}
